import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import MoreMenuOverlay from './MoreMenuOverlay';
import FowlPlayGameMenu from './FowlPlayGameMenu';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 3px solid #ff6b6b;
  padding: 1rem 2rem;
  
  @media (max-width: 768px) {
    padding: 0.8rem 1rem;
  }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Logo = styled(motion.div)`
  display: flex;
  align-items: center;
  cursor: pointer;
  
  @media (max-width: 768px) {
    flex: 0 0 auto;
    max-width: calc(100vw - 120px);
    min-width: 0;
  }
  
  img {
    height: 60px;
    width: auto;
    
    @media (max-width: 768px) {
      height: 45px;
    }
  }
`;

const LogoText = styled.h1`
  margin-left: 1rem;
  color: #ff6b6b;
  font-size: 2rem;
  font-weight: bold;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-left: 0.5rem;
    font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin-left: 0.3rem;
    font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
    max-width: 120px;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNavButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #ff6b6b;
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: block;
    margin-left: 1rem;
    margin-bottom: 0.2rem;
    flex-shrink: 0;
  }
`;

const MobileNav = styled(motion.div)`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    background: rgba(255, 255, 255, 0.98);
    border-top: 2px solid #ff6b6b;
    padding: 1rem 0;
    gap: 1rem;
    align-items: center;
    z-index: 1001;
  }
`;

const NavLink = styled(motion.a)`
  color: #333;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.1rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  transition: all 0.3s ease;
  position: relative;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 1.5rem;
    width: 80%;
    text-align: center;
    background: rgba(255, 107, 107, 0.1);
    border: 2px solid transparent;
    font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
    
    &:hover {
      background: rgba(255, 107, 107, 0.2);
      border-color: #ff6b6b;
    }
  }
  
  &:hover {
    background: #ff6b6b;
    color: white;
    transform: translateY(-2px);
  }
`;

const StoreButton = styled(motion.a)`
  background: linear-gradient(45deg, #ff6b6b, #ffd93d);
  color: white;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: bold;
  font-size: 1.1rem;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 1.5rem;
    width: 80%;
    text-align: center;
    display: block;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
  }
`;

const MobileHeaderShopButton = styled(motion.a)`
  display: none;
  background: linear-gradient(45deg, #ff6b6b, #ffd93d);
  color: white;
  text-decoration: none;
  padding: 0.7rem 1.2rem;
  border-radius: 22px;
  font-weight: bold;
  font-size: 1rem;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
  margin-right: 0.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(255, 107, 107, 0.4);
    color: white;
    text-decoration: none;
  }
`;

const SearchOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const SearchContainer = styled(motion.div)`
  background: linear-gradient(135deg, #ffd93d 0%, #ff6b6b 50%, #6bcf7f 100%);
  border-radius: 20px;
  padding: 3rem;
  max-width: 600px;
  width: 100%;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 15px;
  }
`;

const SearchCloseButton = styled(motion.button)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const SearchTitle = styled(motion.h2)`
  text-align: center;
  font-size: 2.5rem;
  color: white;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SearchSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.1rem;
  color: white;
  margin-bottom: 2rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 15px;
  font-size: 1.2rem;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  transition: all 0.3s ease;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
  
  &:focus {
    outline: none;
    background: white;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
    transform: scale(1.02);
  }
  
  &::placeholder {
    color: #666;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 1.2rem;
  }
`;

const SearchResults = styled(motion.div)`
  margin-top: 2rem;
  max-height: 300px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-height: 250px;
  }
`;

const SearchResult = styled(motion.div)`
  background: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    transform: translateX(5px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
`;

const ResultTitle = styled.h3`
  color: #333;
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 0.3rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
`;

const ResultDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
`;

const NoResults = styled(motion.div)`
  text-align: center;
  color: white;
  font-size: 1rem;
  font-style: italic;
  margin-top: 1rem;
  font-family: 'Comic Sans MS', 'Comic Neue', 'Fredoka One', 'Bubblegum Sans', 'Chalkboard SE', 'Marker Felt', 'Arial Rounded MT Bold', 'Helvetica Rounded', Arial, sans-serif;
`;

const Header = ({ onArtClick, onShopClick, onCartClick, cartItemCount = 0, onShowComingSoon, onGameOpen }) => {
  const [logoHover, setLogoHover] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [moreMenuOverlayOpen, setMoreMenuOverlayOpen] = useState(false);
  const [fowlPlayOverlayOpen, setFowlPlayOverlayOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchInputRef = useRef(null);

  // Website content for search
  const websiteContent = [
    {
      id: 1,
      title: "Chikin Lady NFT Collection",
      description: "Meet our cluckin' NFT collection featuring unique chicken designs",
      section: "NFTs",
      url: "#products"
    },
    {
      id: 2,
      title: "Featured Products",
      description: "Fresh from the coop! Our finest featured products including t-shirts, can coolers, and mugs",
      section: "Store",
      url: "#featured"
    },
    {
      id: 3,
      title: "Chikin Lady Game",
      description: "Play the exciting Chikin Lady game and have fun with our chicken characters",
      section: "Play",
      url: "#"
    },
    {
      id: 4,
      title: "Art Gallery",
      description: "Explore our collection of Chikin Lady artwork and designs",
      section: "Art",
      url: "#"
    },
    {
      id: 5,
      title: "About Chikin Lady",
      description: "Learn about the story behind Chikin Lady and our mission",
      section: "About",
      url: "#about"
    },
    {
      id: 6,
      title: "Shop Now",
      description: "Visit our official store to buy Chikin Lady merchandise",
      section: "Store",
      url: "https://chikinlady.printful.me/?sort=name"
    },
    {
      id: 7,
      title: "NFT Raffle",
      description: "Register for our NFT raffle and win exclusive Chikin Lady merchandise",
      section: "NFTs",
      url: "#products"
    },
    // NFT Products
    {
      id: 8,
      title: "Chikin Lady",
      description: "The iconic Chikin Lady logo! Our signature design that started it all.",
      section: "NFTs",
      url: "#products"
    },
    {
      id: 9,
      title: "Bandana Chikin",
      description: "Rock that bandana look with our sassy chicken design! Perfect for any occasion.",
      section: "NFTs",
      url: "#products"
    },
    {
      id: 10,
      title: "Bikini Chikin",
      description: "Beach ready with our fabulous chicken in a bikini! Summer vibes all year round.",
      section: "NFTs",
      url: "#products"
    },
    {
      id: 11,
      title: "Freakn' Chikin",
      description: "The most badass chicken you'll ever meet! For those who dare to be different.",
      section: "NFTs",
      url: "#products"
    },
    {
      id: 12,
      title: "Fried Chicken",
      description: "Our signature fried chicken design! Crispy, golden, and absolutely delicious.",
      section: "NFTs",
      url: "#products"
    },
    // Featured Products
    {
      id: 13,
      title: "Chikin Lady Classic Tee",
      description: "Premium cotton t-shirt featuring the iconic Chikin Lady design. Available in multiple colors and sizes for the ultimate chicken lover style!",
      section: "Store",
      url: "#featured"
    },
    {
      id: 14,
      title: "Chikin Lady Can Cooler",
      description: "Keep your drinks cold with style! This insulated can cooler features the beloved Chikin Lady design. Perfect for parties, picnics, or just showing off your chicken love!",
      section: "Store",
      url: "#featured"
    },
    {
      id: 15,
      title: "Chikin Lady Mug",
      description: "Start your day with a smile! This ceramic mug features the Chikin Lady design and is microwave safe. Perfect for coffee, tea, or any hot beverage!",
      section: "Store",
      url: "#featured"
    }
  ];

  const handleShopClick = () => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-1047967012/9GnhCNaIzIAcEKTq2vMD'
      });
      console.log('Conversion tracking event sent (Shop Now).');
    }
  };

  const handleGameClick = () => {
    onGameOpen();
    setFowlPlayOverlayOpen(false);
    setMobileNavOpen(false);
  };

  const handleGetAppClick = () => {
    setMoreMenuOverlayOpen(false);
    setMobileNavOpen(false);
    // Add a small delay to ensure the overlay closes before showing the modal
    setTimeout(() => {
      onShowComingSoon();
    }, 100);
  };

  const handleSpotifyClick = () => {
    setMoreMenuOverlayOpen(false);
    setMobileNavOpen(false);
    // Add a small delay to ensure the overlay closes before showing the modal
    setTimeout(() => {
      onShowComingSoon();
    }, 100);
  };

  const handleYouTubeClick = () => {
    setMoreMenuOverlayOpen(false);
    setMobileNavOpen(false);
    window.open('https://www.youtube.com/@ChikinLady', '_blank');
  };

  const handleTalesClick = () => {
    setMoreMenuOverlayOpen(false);
    setMobileNavOpen(false);
    // Add a small delay to ensure the overlay closes before showing the modal
    setTimeout(() => {
      onShowComingSoon();
    }, 100);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim() === '') {
      setSearchResults([]);
      return;
    }

    const results = websiteContent.filter(item =>
      item.title.toLowerCase().includes(query.toLowerCase()) ||
      item.description.toLowerCase().includes(query.toLowerCase()) ||
      item.section.toLowerCase().includes(query.toLowerCase())
    );
    setSearchResults(results);
  };

  const handleSearchClick = () => {
    setSearchOpen(true);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 300);
  };

  const handleResultClick = (result) => {
    if (result.url.startsWith('http')) {
      window.open(result.url, '_blank');
    } else {
      // Scroll to section
      const element = document.querySelector(result.url);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };


  return (
    <>
      <HeaderContainer>
        <Nav>
          <Logo
            onHoverStart={() => setLogoHover(true)}
            onHoverEnd={() => setLogoHover(false)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSearchClick}
            style={{ cursor: 'pointer' }}
          >
            <motion.img
              src="/assets/transparentchikinladyLOGO-2.png"
              alt="Chikin Lady Logo"
              animate={{
                rotate: logoHover ? [0, -10, 10, 0] : 0,
                scale: logoHover ? 1.1 : 1
              }}
              transition={{ duration: 0.5 }}
            />
            <LogoText>Chikin Lady</LogoText>
          </Logo>

          <MobileHeaderShopButton
            href="https://chikinlady.printful.me/?sort=name"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleShopClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            🛒 Visit Store
          </MobileHeaderShopButton>

          <MobileNavButton
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            aria-label="Toggle navigation menu"
          >
            {mobileNavOpen ? '✕' : '☰'}
          </MobileNavButton>

          <NavLinks>
            <NavLink
              href="#home"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Home
            </NavLink>

            <NavLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onArtClick();
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Art
            </NavLink>
            <NavLink
              href="#products"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              NFT's
            </NavLink>
            <NavLink
              href="#about"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              About
            </NavLink>
            <StoreButton
              href="https://chikinlady.printful.me/?sort=name"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleShopClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🛒 Shop Now
            </StoreButton>
            <StoreButton
              href="https://chikinbox.com"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{ background: 'linear-gradient(45deg, #6bcf7f, #4ecdc4)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <img
                src="/assets/chikinbox-icon.png"
                alt="CHIKINBOX"
                style={{
                  height: '32px',
                  width: '32px',
                  borderRadius: '50%',
                  objectFit: 'contain'
                }}
              />
              CHIKINBOX
            </StoreButton>
          </NavLinks>
        </Nav>

        {mobileNavOpen && (
          <MobileNav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <NavLink
              href="#home"
              onClick={() => setMobileNavOpen(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Home
            </NavLink>

            <NavLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onArtClick();
                setMobileNavOpen(false);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Art
            </NavLink>
            <NavLink
              href="#products"
              onClick={() => setMobileNavOpen(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              NFT's
            </NavLink>
            <NavLink
              href="#about"
              onClick={() => setMobileNavOpen(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              About
            </NavLink>
            <StoreButton
              href="https://chikinlady.printful.me/?sort=name"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                handleShopClick();
                setMobileNavOpen(false);
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🛒 Shop Now
            </StoreButton>
            <StoreButton
              href="https://chikinbox.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileNavOpen(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                background: 'linear-gradient(45deg, #6bcf7f, #4ecdc4)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                justifyContent: 'center'
              }}
            >
              <img
                src="/assets/chikinbox-icon.png"
                alt="CHIKINBOX"
                style={{
                  height: '32px',
                  width: '32px',
                  borderRadius: '50%',
                  objectFit: 'contain'
                }}
              />
              CHIKINBOX
            </StoreButton>
          </MobileNav>
        )}
      </HeaderContainer>

      <AnimatePresence>
        {searchOpen && (
          <SearchOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <SearchContainer
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <SearchCloseButton
                onClick={() => {
                  setSearchOpen(false);
                  setSearchQuery('');
                  setSearchResults([]);
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                ✕
              </SearchCloseButton>

              <SearchTitle
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <img
                  src="/assets/transparentchikinladyLOGO-2.png"
                  alt="Chikin Lady"
                  style={{
                    height: '3rem',
                    width: 'auto',
                    verticalAlign: 'middle',
                    marginRight: '0.8rem',
                    filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3))'
                  }}
                />
                Search Chikin Lady
              </SearchTitle>

              <SearchSubtitle
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Find what you're looking for on our website!
              </SearchSubtitle>

              <SearchInput
                ref={searchInputRef}
                type="text"
                placeholder="Search for NFTs, products, games, art..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              />

              <SearchResults
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                {searchResults.map((result, index) => (
                  <SearchResult
                    key={result.id}
                    onClick={() => handleResultClick(result)}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <ResultTitle>{result.title}</ResultTitle>
                    <ResultDescription>{result.description}</ResultDescription>
                  </SearchResult>
                ))}

                {searchQuery && searchResults.length === 0 && (
                  <NoResults
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    No results found for "{searchQuery}"
                  </NoResults>
                )}
              </SearchResults>
            </SearchContainer>
          </SearchOverlay>
        )}
      </AnimatePresence>

      <MoreMenuOverlay
        isOpen={moreMenuOverlayOpen}
        onClose={() => setMoreMenuOverlayOpen(false)}
        onGetAppClick={handleGetAppClick}
        onSpotifyClick={handleSpotifyClick}
        onYouTubeClick={handleYouTubeClick}
        onTalesClick={handleTalesClick}
      />

      <FowlPlayGameMenu
        isOpen={fowlPlayOverlayOpen}
        onClose={() => setFowlPlayOverlayOpen(false)}
        onGameClick={handleGameClick}
      />
    </>
  );
};

export default Header;
